import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApiData } from "../hooks/useApiData";
import "./PageContent.css";
import "./SiteMapPage.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://192.168.0.57:5001/api";

function slugify(name = "") {
  return String(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function pageHref(item, parentPath = "") {
  if (item.page_url) return item.page_url;
  const slug = slugify(item.page_name);
  return parentPath ? `/${parentPath}/${slug}` : `/${slug}`;
}

function getChildren(item) {
  // Same fallback chain as QuickLinks — handles whatever key name
  // the /menus payload actually nests children under.
  return (
    item.children ||
    item.sub_menus ||
    item.submenu ||
    item.child_pages ||
    item.subpages ||
    []
  );
}

// depth 0 = main (gray box), 1 = sub (green), 2+ = sub-sub (blue)
function SiteMapNode({ item, parentPath, depth }) {
  const children = getChildren(item);
  const hasChildren = children.length > 0;
  const path = parentPath
    ? `${parentPath}/${slugify(item.page_name)}`
    : slugify(item.page_name);
  const href = pageHref(item, parentPath);

  const boxClass =
    depth === 0
      ? "sitemap-main-box"
      : depth === 1
      ? "sitemap-sub-link"
      : "sitemap-subsub-link";

  const rowClass =
    depth === 0
      ? "sitemap-main-block"
      : depth === 1
      ? "sitemap-sub-row"
      : "sitemap-subsub-row";

  const listClass = depth === 0 ? "sitemap-sub-list" : "sitemap-subsub-list";

  const label = (
    <>
      {item.page_name} <span className="sitemap-arrow">&raquo;</span>
    </>
  );

  return (
    <div className={rowClass}>
      {href && href !== "/" ? (
        <a
          href={href}
          target={item.page_url ? "_blank" : undefined}
          rel={item.page_url ? "noopener noreferrer" : undefined}
          className={boxClass}
        >
          {label}
        </a>
      ) : (
        <span className={boxClass}>{label}</span>
      )}

      {hasChildren && (
        <div className={listClass}>
          {children.map((child) => (
            <SiteMapNode
              key={child.page_id}
              item={child}
              parentPath={path}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function SiteMapPage() {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Leadership sidebar — same source as WebLinkPage, via useApiData
  const { data: leaders } = useApiData("leadership");

  const principal = leaders?.find((l) =>
    String(l.role || "").toLowerCase().match(/principal|dean/)
  );
  const medicalSuperintendent = leaders?.find((l) =>
    String(l.role || "").toLowerCase().includes("medical superintendent")
  );

  const slugifyRole = (str) =>
    String(str || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const renderOfficialMessage = (leader, title) => {
    if (!leader) {
      return null;
    }

    return (
      <div className="official-message-block">
        <div className="official-message-title">{title}</div>

        <div className="official-message-body">
          <div className="official-message-top">
            <div className="official-photo-wrapper">
              <img
                src={leader.img}
                alt={leader.name}
                className="official-photo"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    leader.name || "Official"
                  )}&background=0b5d7a&color=fff&size=150`;
                }}
              />
            </div>

            <div className="official-message-text">
              <p>{leader.message}</p>
            </div>
          </div>

          <div className="official-name">{leader.name}</div>

          <div className="official-role">{leader.role}</div>

          <div className="official-more-wrapper">
            <Link
              to={`/leadership-message/${slugifyRole(leader.role)}`}
              className="official-more"
            >
              More...
            </Link>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetch(`${BASE_URL}/menus`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setMenus(Array.isArray(data) ? data : []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="content-page-wrapper">
      <div className="content-breadcrumb-wrapper">
        <div className="content-container">
          <div className="content-breadcrumb">
            <Link to="/">
              <i className="fa fa-home"></i>
              Home
            </Link>

            <span className="breadcrumb-arrow">
              <i className="fa fa-angle-right"></i>
            </span>

            <span className="breadcrumb-current">Site Map</span>
          </div>
        </div>
      </div>

      <div className="content-container">
        <div className="content-layout">
          <aside className="content-sidebar">
            <div className="official-info-card">
              <div className="official-info-header">
                <div className="official-info-icon">
                  <i className="fa fa-envelope-o"></i>
                </div>
                <span>MESSAGE FROM</span>
              </div>

              <div className="official-info-body">
                {renderOfficialMessage(principal, "DEAN AND PRINCIPAL")}
                {renderOfficialMessage(
                  medicalSuperintendent,
                  "MEDICAL SUPERINTENDENT"
                )}
              </div>
            </div>
          </aside>

          <section className="content-main-card sitemap-card">
            <h1 className="notice-page-title">Site Map</h1>

            {error && (
              <p className="ql-error">
                <i className="fa fa-exclamation-circle" aria-hidden="true" />{" "}
                Could not load site map
              </p>
            )}

            {loading ? (
              <div className="content-loading">
                <div className="content-spinner"></div>
                <p>Loading site map...</p>
              </div>
            ) : menus.length > 0 ? (
              <div className="sitemap-tree">
                {menus.map((item) => (
                  <SiteMapNode
                    key={item.page_id}
                    item={item}
                    parentPath=""
                    depth={0}
                  />
                ))}
              </div>
            ) : (
              <p className="notice-card-empty">No site map found.</p>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
